/**
 * Left pipeline rail of the batch workspace (UX plan §4.4): clickable
 * non-linear stepper Brief → Angles → Creatives → Launch with live status
 * lines, plus a compact brief summary card with an Edit shortcut.
 */

import React from 'react';
import {
  Box,
  Button,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import type { AdCreativeBrief } from '@segmently/schema-models/ad-creative';
import { AD_CREATIVE_BATCH } from '../../selectors';
import type { BatchStepKey, BatchStepStatus } from './batchModel';

interface BatchStepRailProps {
  steps: BatchStepStatus[];
  activeStep: BatchStepKey;
  brief: AdCreativeBrief | null;
  onSelectStep: (step: BatchStepKey) => void;
  onEditBrief: () => void;
}

export function BatchStepRail({ steps, activeStep, brief, onSelectStep, onEditBrief }: BatchStepRailProps): React.ReactElement {
  const activeIndex = Math.max(0, steps.findIndex((step) => step.key === activeStep));
  return (
    <Box data-testid={AD_CREATIVE_BATCH.rail} sx={{ width: { xs: '100%', md: 248 }, flexShrink: 0 }}>
      <Stepper nonLinear orientation="vertical" activeStep={activeIndex}>
        {steps.map((step) => (
          <Step key={step.key} completed={step.complete}>
            <StepButton
              data-testid={AD_CREATIVE_BATCH.railStep(step.key)}
              onClick={() => onSelectStep(step.key)}
            >
              <StepLabel
                optional={(
                  <Typography variant="caption" color="text.secondary">
                    {step.statusLine}
                  </Typography>
                )}
              >
                {step.label}
              </StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>
      {brief && (
        <Box
          data-testid={AD_CREATIVE_BATCH.briefSummary}
          sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
        >
          <Typography variant="caption" color="text.secondary">Brief</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{brief.title}</Typography>
          {brief.objective && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Goal: {brief.objective}
            </Typography>
          )}
          {brief.designCollectionId && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
              Style: {brief.designCollectionId}
            </Typography>
          )}
          <Button
            data-testid={AD_CREATIVE_BATCH.editBrief}
            size="small"
            sx={{ mt: 1 }}
            onClick={onEditBrief}
          >
            Edit brief
          </Button>
        </Box>
      )}
    </Box>
  );
}
